// ═══════════════════════════════════════════════════════════════════════════
// PILOTS FORM — pilotes, pilotes de comités et onglets, partagés projet ↔ programme
//
// Audit dette 2026-09-13 (D4b) : `page-programs.ts` recopiait `page-projects.ts`
// (tri et cellule « pilotes » de la liste, chargement users + comités, puces,
// lignes « pilote de comité », onglets de modale et leur câblage), et sa modale
// de création recopiait sa page d'édition. Les copies ne différaient que par
// le préfixe des classes CSS (`pm-` projet, `pgm-` programme) et les clés i18n —
// d'où le paramètre `prefix` et les libellés DÉJÀ traduits par l'appelant,
// comme `utils/pilot-picker`. Les ids et classes gardent leur nom d'origine sur
// chaque page (les e2e les ciblent).
//
// Les builders `build*` sont PURS (chaîne en sortie) ; les `wire*` /
// `read*` touchent le DOM et s'appellent une fois le markup posé.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : api, esc, t.

export interface UserMin {
  id: number;
  trigram?: string | null;
  name?: string;
  is_team?: boolean;
}

export interface CommitteeRow {
  id: number;
  code: string;
  name: string;
}

export interface CommitteePilot {
  committee_id: number;
  user_id: number;
}

export type UserMap = Record<number, UserMin>;

/** Utilisateurs individuels + comités. Si l'un des deux appels échoue, la
 * modale s'ouvre quand même, sans ces blocs (comportement d'origine). */
export async function loadPilotRefs(): Promise<{
  indivUsers: UserMin[];
  committees: CommitteeRow[];
}> {
  let users: UserMin[] = [];
  let committees: CommitteeRow[] = [];
  try {
    [users, committees] = (await Promise.all([api.getUsers(), api.getCommittees()])) as [
      UserMin[],
      CommitteeRow[],
    ];
  } catch {
    /* on continue sans les blocs */
  }
  return { indivUsers: (users || []).filter((u) => !u.is_team), committees };
}

// ── Liste : colonne « Pilotes » ─────────────────────────────────────────────

/** Clé de tri d'une ligne sur ses pilotes : premier trigramme (ordre alpha),
 * les lignes sans pilote en fin de liste. */
export function pilotsSortKey(pilots: number[] | undefined, userMap: UserMap): string {
  const first =
    (pilots || [])
      .map((id) => userMap[id]?.trigram || '')
      .filter(Boolean)
      .sort()[0] || 'zzzz';
  return first.toLowerCase();
}

/** Cellule « Pilotes » : un badge par trigramme, ou ⚠ (avec `emptyTitle`). */
export function buildPilotsCell(
  pilots: number[] | undefined,
  userMap: UserMap,
  emptyTitle: string,
): string {
  const ids = pilots || [];
  if (!ids.length) {
    return (
      '<span style="color:var(--warn);cursor:help;font-size:14px"' +
      ` title="${esc(emptyTitle)}">⚠</span>`
    );
  }
  return ids
    .map((id) => {
      const u = userMap[id];
      if (!u) return '<span style="color:var(--text3)">?</span>';
      return `<span class="text-mono" style="font-weight:600;background:var(--bg3);padding:1px 6px;border-radius:4px;font-size:11px;margin-right:3px" title="${esc(u.name || '')}">${esc(u.trigram || '?')}</span>`;
    })
    .join('');
}

// ── Pilotes : puces et onglet ───────────────────────────────────────────────

/** Puces des pilotes sélectionnés (vide → chaîne vide ; l'appelant fournit son
 * propre libellé « aucun pilote »). Classes `<prefix>-pilot-chip` / `-remove`. */
export function buildPilotChips(
  selectedPilots: Set<number>,
  indivUsers: ReadonlyArray<UserMin>,
  prefix: string,
): string {
  return Array.from(selectedPilots)
    .map((id) => indivUsers.find((u) => u.id === id))
    .filter((u): u is UserMin => Boolean(u))
    .map(
      (u) => `<span class="${prefix}-pilot-chip" data-uid="${u.id}" title="${esc(u.name || '')}"
        style="display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);
               font-size:11px;padding:2px 7px;border-radius:10px;margin:2px;cursor:default">
        ${esc(u.trigram || u.name || '')}
        <button type="button" class="${prefix}-pilot-remove" data-uid="${u.id}"
          style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>
      </span>`,
    )
    .join('');
}

/** Onglet « Pilotes » de la modale : aide, bloc de puces et bouton « + ».
 * `helpHtml` est inséré tel quel (texte i18n) ; `addTitle` est échappé. */
export function buildPilotsTab(
  prefix: string,
  chipsHtml: string,
  labels: { helpHtml: string; addTitle: string },
): string {
  return `
    <p style="font-size:11px;color:var(--text3);margin:0 0 8px">
      ${labels.helpHtml}
    </p>
    <div id="${prefix}-pilots-block"
      style="border:1px solid var(--border2);border-radius:var(--radius);padding:6px 8px;display:flex;align-items:flex-start;gap:6px">
      <div id="${prefix}-pilots-chips" style="flex:1;min-height:22px;display:flex;flex-wrap:wrap;gap:2px">${chipsHtml}</div>
      <button type="button" id="${prefix}-pilot-add-btn" class="btn btn-secondary btn-icon"
        style="flex-shrink:0;font-size:14px;line-height:1;padding:3px 7px;align-self:flex-start" title="${esc(labels.addTitle)}">+</button>
    </div>`;
}

/** Rafraîchit les puces (`#<prefix>-pilots-chips`) et câble leurs boutons ×.
 * Renvoie la fonction de rafraîchissement, à repasser au picker (`onClose`). */
export function createPilotChipsRefresher(
  prefix: string,
  renderChips: () => string,
  selectedPilots: Set<number>,
  onRemove?: () => void,
): () => void {
  const refreshChips = (): void => {
    const el = document.getElementById(`${prefix}-pilots-chips`);
    if (el) el.innerHTML = renderChips();
    document.querySelectorAll<HTMLElement>(`.${prefix}-pilot-remove`).forEach((b) => {
      b.addEventListener('click', (ev: Event) => {
        ev.stopPropagation();
        selectedPilots.delete(parseInt(b.dataset['uid'] || '0'));
        onRemove?.();
        refreshChips();
      });
    });
  };
  return refreshChips;
}

// ── Pilotes de comités : lignes, pastilles, lecture ─────────────────────────

/** `<option>` par utilisateur individuel, précédées du choix vide « — ». */
export function buildUserOptions(indivUsers: ReadonlyArray<UserMin>): string {
  return ['<option value="">—</option>']
    .concat(
      indivUsers.map(
        (u) =>
          `<option value="${u.id}">${esc(u.name || '')}${u.trigram ? ' (' + esc(u.trigram) + ')' : ''}</option>`,
      ),
    )
    .join('');
}

/** `{ committee_id → user_id }` à partir de la liste renvoyée par l'API. */
export function indexCommitteePilots(
  committeePilots: ReadonlyArray<CommitteePilot> | undefined,
): Record<number, number> {
  const byCid: Record<number, number> = {};
  for (const cp of committeePilots || []) byCid[cp.committee_id] = cp.user_id;
  return byCid;
}

/** Les trois morceaux d'une ligne « pilote de comité », à composer par la
 * mise en page de l'appelant : pastille, « CODE Nom », sélecteur. */
export interface CommitteePilotRowParts {
  dotHtml: string;
  codeNameHtml: string;
  selectHtml: string;
}

export interface CommitteePilotRowsOpts {
  prefix: string;
  /** Infobulle de la pastille orange (aucun pilote). */
  missingLabel: string;
  /** Libellé rendu quand le tenant n'a aucun comité. */
  emptyLabel: string;
  /** Mise en page d'une ligne ; par défaut, la grille sur une ligne des modales. */
  row?: (c: CommitteeRow, parts: CommitteePilotRowParts) => string;
}

const modalCommitteeRow = (
  prefix: string,
  c: CommitteeRow,
  parts: CommitteePilotRowParts,
): string => `
      <div class="${prefix}-cp-row" data-cid="${c.id}" style="display:grid;grid-template-columns:14px 220px 1fr;gap:10px;align-items:center;padding:5px 0">
        ${parts.dotHtml}
        <span style="font-size:12px;color:var(--text2)">${parts.codeNameHtml}</span>
        ${parts.selectHtml}
      </div>`;

/** Une ligne par comité (pastille + sélecteur de pilote présélectionné), ou
 * le libellé « aucun comité ». Classes `<prefix>-cp-row` / `-cp-dot` / `-cp`. */
export function buildCommitteePilotRows(
  committees: ReadonlyArray<CommitteeRow>,
  initialByCid: Record<number, number>,
  indivUsers: ReadonlyArray<UserMin>,
  opts: CommitteePilotRowsOpts,
): string {
  if (!committees || !committees.length) {
    return `<div style="color:var(--text3);font-size:11px;font-style:italic">${esc(opts.emptyLabel)}</div>`;
  }
  const { prefix } = opts;
  const userOpts = buildUserOptions(indivUsers);
  const row = opts.row ?? ((c, parts) => modalCommitteeRow(prefix, c, parts));
  return committees
    .map((c) => {
      const sel = initialByCid[c.id] || '';
      const optsHtml = userOpts.replace(`value="${sel}"`, `value="${sel}" selected`);
      return row(c, {
        dotHtml: `<span class="${prefix}-cp-dot" title="${sel ? '' : esc(opts.missingLabel)}" style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${sel ? 'var(--accent)' : 'var(--warn)'}"></span>`,
        codeNameHtml: `<span style="font-family:var(--font-mono);font-size:11px;font-weight:600;color:var(--text3);margin-right:6px">${esc(c.code)}</span>${esc(c.name)}`,
        selectHtml: `<select class="${prefix}-cp" data-cid="${c.id}" style="font-size:12px;padding:4px 6px;border:1px solid var(--border2);border-radius:var(--radius);width:100%">${optsHtml}</select>`,
      });
    })
    .join('');
}

/** Pastille d'une ligne comité : verte dès qu'un pilote est choisi, orange
 * (avec `missingLabel` en infobulle) sinon. */
export function wireCommitteePilotDots(prefix: string, missingLabel: string): void {
  document.querySelectorAll<HTMLSelectElement>(`.${prefix}-cp`).forEach((sel) => {
    sel.addEventListener('change', () => {
      const dot = sel
        .closest<HTMLElement>(`.${prefix}-cp-row`)
        ?.querySelector<HTMLElement>(`.${prefix}-cp-dot`);
      if (!dot) return;
      if (sel.value) {
        dot.style.background = 'var(--accent)';
        dot.title = '';
      } else {
        dot.style.background = 'var(--warn)';
        dot.title = missingLabel;
      }
    });
  });
}

/** Pilotes de comités saisis (les comités sans pilote sont omis). */
export function readCommitteePilots(prefix: string): CommitteePilot[] {
  return Array.from(document.querySelectorAll<HTMLSelectElement>(`.${prefix}-cp`))
    .map((s) => ({
      committee_id: parseInt(s.dataset['cid'] || '0'),
      user_id: parseInt(s.value) || null,
    }))
    .filter((x): x is CommitteePilot => x.user_id !== null);
}

// ── Onglets de la modale (Général / Pilotes / Comités) ──────────────────────

/** Style inline d'un bouton d'onglet (actif / inactif). */
export const modalTabBtnStyle = (active: boolean): string =>
  active
    ? 'padding:10px 18px;cursor:pointer;font-size:13px;font-weight:700;' +
      'background:var(--bg);color:var(--accent);' +
      'border:1px solid var(--border);border-bottom:1px solid var(--bg);' +
      'border-top:3px solid var(--accent);' +
      'border-radius:6px 6px 0 0;margin-bottom:-1px;position:relative;z-index:2'
    : 'padding:10px 18px;cursor:pointer;font-size:13px;font-weight:500;' +
      'background:var(--bg2);color:var(--text2);' +
      'border:1px solid transparent;border-bottom:1px solid var(--border);' +
      'border-radius:6px 6px 0 0;margin-bottom:-1px';

export interface ModalTab {
  /** Libellé du bouton, déjà traduit (échappé ici). */
  label: string;
  /** Contenu du panneau, déjà composé et échappé. */
  html: string;
}

const TAB_NAMES = ['general', 'pilots', 'committees'] as const;

/** Barre d'onglets + trois panneaux ; « Général » ouvert. */
export function buildModalTabs(
  prefix: string,
  tabs: Record<(typeof TAB_NAMES)[number], ModalTab>,
): string {
  return `
    <div class="${prefix}-tabs" style="display:flex;gap:4px;border-bottom:1px solid var(--border);margin-bottom:14px;padding:0 4px">
      <button type="button" class="${prefix}-tab-btn" data-tab="general" style="${modalTabBtnStyle(true)}">${esc(tabs.general.label)}</button>
      <button type="button" class="${prefix}-tab-btn" data-tab="pilots" style="${modalTabBtnStyle(false)}">${esc(tabs.pilots.label)}</button>
      <button type="button" class="${prefix}-tab-btn" data-tab="committees" style="${modalTabBtnStyle(false)}">${esc(tabs.committees.label)}</button>
    </div>
    <div class="${prefix}-tab-panel" style="min-height:420px">
      <div class="${prefix}-tab ${prefix}-tab-general" style="padding:6px 4px">${tabs.general.html}</div>
      <div class="${prefix}-tab ${prefix}-tab-pilots" style="display:none;padding:6px 4px">${tabs.pilots.html}</div>
      <div class="${prefix}-tab ${prefix}-tab-committees" style="display:none;padding:6px 4px">${tabs.committees.html}</div>
    </div>`;
}

/** Bascule d'onglet au clic : style du bouton actif, panneau visible. */
export function wireModalTabs(prefix: string): void {
  const buttons = document.querySelectorAll<HTMLElement>(`.${prefix}-tab-btn`);
  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      buttons.forEach((b) => {
        b.style.cssText = modalTabBtnStyle(b === btn);
      });
      const target = btn.dataset['tab'];
      for (const name of TAB_NAMES) {
        const panel = document.querySelector<HTMLElement>(`.${prefix}-tab-${name}`);
        if (panel) panel.style.display = target === name ? '' : 'none';
      }
    });
  });
}

// ── Champ code ──────────────────────────────────────────────────────────────

/** Force la saisie de `#pm-code` en majuscules. */
export function wireCodeUppercase(): void {
  document.getElementById('pm-code')?.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (target) target.value = target.value.toUpperCase();
  });
}
